<script>
  import { navigate } from 'svelte-routing';
  import { getApiUrl } from '../api';
  import { cloneStore, scanStatus } from '../cloneStore.js';
  import { t, currentLang } from '../i18nStore'; // Import i18n
  import { authFetch } from '../authService';
  import { selectedCountry } from '../countryStore';
  import { notificationStore } from '../notificationStore.js';

  let isLoading = false;
  let error = '';
  let fileInput;
  let urlInput = '';

  function startBackgroundScan() {
    scanStatus.set({ isScanning: true, error: null, data: null });
    notificationStore.set({
      message: "Data extraction started in background. The backend process is running, please wait...",
      type: "admin"
    });
    navigate('/add');
  }

  // --- EXISTING IMAGE SCAN ---
  async function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    error = '';

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = async () => {
      const base64String = reader.result.split(',')[1]; 
      startBackgroundScan();
      try {
        const response = await authFetch(getApiUrl('/promotions/scan-image'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageBase64: base64String,
            language: $currentLang,
          }),
        });
        await handleResponse(response);
      } catch (err) {
        scanStatus.set({ isScanning: false, error: err.message, data: null });
        notificationStore.set({
          message: "Data extraction failed: " + err.message,
          type: "admin"
        });
      }
    };
    reader.onerror = () => {
      error = 'Failed to read image file.';
      scanStatus.set({ isScanning: false, error: error, data: null });
      notificationStore.set({
        message: "Data extraction failed: Failed to read image file.",
        type: "admin"
      });
    };
  }

  // --- EXISTING URL SCAN ---
  async function handleUrlScan() {
    if (!urlInput) return;
    error = '';

    const payload = {
        url: urlInput,
        countryCode: $selectedCountry
    };
    
    startBackgroundScan();
    try {
        const response = await authFetch(getApiUrl('/promotions/scan-url'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        await handleResponse(response);
    } catch (err) {
        scanStatus.set({ isScanning: false, error: err.message, data: null });
        notificationStore.set({
          message: "Data extraction failed: " + err.message,
          type: "admin"
        });
    }
  }

  async function handleResponse(response) {
      if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.error || 'Scan failed.');
      }
      const data = await response.json();
      
      const extractedData = {
          name: data.name || '',
          description: data.description || '',
          startDate: data.startDate || '',
          endDate: data.endDate || '',
          categories: data.categories || [],
          banks: data.banks || [],
          creditCards: data.creditCards || [],
          isOnline: data.isOnline || false,
          isInStore: data.isInStore || false,
          requireRegistration: data.requireRegistration || false,
          minimumSpending: data.minimumSpending || '',
          methodsOfSpending: data.methodsOfSpending || [],
          maximumReward: data.maximumReward || '',
          maximumRewardPercentage: data.maximumRewardPercentage || '',
          eligibleDates: data.eligibleDates || [],
          rewardTiers: data.rewardTiers || [],
          maximumTargetNumTransactions: data.maximumTargetNumTransactions || '',
          maximumTargetAccumulatedSpending: data.maximumTargetAccumulatedSpending || '',
          eligibleDaysOfWeek: data.eligibleDaysOfWeek || [],
          hyperlink: urlInput || '' 
      };

      scanStatus.set({ isScanning: false, error: null, data: extractedData });
      cloneStore.set(extractedData);

      notificationStore.set({
          message: "Data extraction completed! Auto-filled the promotion details form.",
          type: "info"
      });
  }

  // --- NEW: Manual Entry ---
  function handleManual() {
      navigate('/add');
  }
</script>

<div class="scan-container">
  <h2>{$t('scan.title')}</h2>
  <p>{$t('scan.subtitle')}</p>

  <div class="options-grid">
      <!-- Option 1: Image -->
      <div class="scan-card">
        <h3>📷 {$t('scan.option1')}</h3>
        <p>{$t('scan.option1_desc')}</p>
        <input
            type="file"
            accept="image/png, image/jpeg"
            on:change={handleFileChange}
            bind:this={fileInput}
            style="display: none;"
            id="file-uploader"
        />
        <button class="action-btn" on:click={() => fileInput.click()} disabled={isLoading}>
            {isLoading ? $t('scan.scanning') : $t('scan.upload_btn')}
        </button>
      </div>

      <!-- Option 2: URL -->
      <div class="scan-card">
        <h3>🔗 {$t('scan.option2')}</h3>
        <p>{$t('scan.option2_desc')}</p>
        <input type="url" bind:value={urlInput} placeholder="https://..." class="url-input" />
        <button class="action-btn" on:click={handleUrlScan} disabled={isLoading || !urlInput}>
            {isLoading ? $t('scan.scanning') : $t('scan.extract_btn')}
        </button>
      </div>
  </div>
  
  <div class="divider">
      <span>{$t('scan.or')}</span>
  </div>

  <!-- NEW: Manual Entry Button -->
  <button class="manual-btn" on:click={handleManual}>
      {$t('scan.manual_btn')}
  </button>

  {#if error}
    <p class="error">{error}</p>
  {/if}
</div>

<style>
  .scan-container { max-width: 800px; margin: 2rem auto; padding: 0 1rem; text-align: center; }
  
  .options-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 2rem;
      margin-top: 2rem;
  }
  @media(min-width: 600px) {
      .options-grid { grid-template-columns: 1fr 1fr; }
  }

  .scan-card {
      background: #fff;
      padding: 2rem;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.05);
      border: 1px solid #eee;
      display: flex;
      flex-direction: column;
      align-items: center;
  }
  
  h3 { margin-top: 0; color: #0056b3; }
  p { color: #666; font-size: 0.9rem; margin-bottom: 1.5rem; }

  .url-input {
      width: 100%;
      padding: 0.75rem;
      margin-bottom: 1rem;
      border: 1px solid #ccc;
      border-radius: 4px;
      font-size: 1rem;
      box-sizing: border-box;
  }

  .action-btn {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    width: 100%;
    transition: background-color 0.2s;
  }
  .action-btn:hover { background-color: #0056b3; }
  .action-btn:disabled { background-color: #aaa; cursor: not-allowed; }
  
  .divider {
      display: flex;
      align-items: center;
      margin: 2rem 0;
      color: #999;
      font-weight: bold;
      font-size: 0.9rem;
  }
  .divider::before, .divider::after {
      content: "";
      flex: 1;
      border-bottom: 1px solid #eee;
  }
  .divider span { padding: 0 1rem; }

  .manual-btn {
      background: none;
      border: 2px solid #ccc;
      color: #555;
      padding: 0.75rem 2rem;
      border-radius: 30px;
      font-size: 1rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
  }
  .manual-btn:hover {
      border-color: #007bff;
      color: #007bff;
      background-color: #f8f9fa;
  }
  
  .error { color: red; margin-top: 2rem; font-weight: bold; background: #fff5f5; padding: 1rem; border-radius: 4px; }
</style>